import { AdministrationStore } from "@/Administration/Stores/AdministrationStore";
import { PrivateBillCostCenter } from "@/Common/Components/Billing/PrivateBillCostCenter";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { Site } from "@/Common/Models/Sites/ResponseModels/Site";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { SitesUIState } from "@/Common/Models/UI/Enums";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { BaseServerOperationHandler } from "@/Common/Stores/Handlers/Server/BaseServerOperationHandler";

class SitesTabResponseHandler extends BaseServerOperationHandler {
    public helper: MyleHelper;

    constructor(action: BaseAction) {
        super(action);
        this.helper = new MyleHelper();

        this.RegisterOperation(ServerOperations.Server.Myle_AdministrationGetSites, this.loadSites);
        this.RegisterOperation(ServerOperations.Server.Myle_Administration_Site_Upsert, this.upsertSite);
        this.RegisterOperation(ServerOperations.Server.Myle_Administration_Site_LoadCostCenter, this.getCostCenters);
        this.RegisterOperation(ServerOperations.Server.Myle_Administration_Site_UploadLogo, this.saveLogo);
    }

    private loadSites(response: ServerEvent, store: AdministrationStore): void {
        const responseData = response.Data["SiteCache"];

        const allSites: Site[] = responseData.map(item => {
            item.LogoAttachments = item.LogoAttachments?.map(logo => {
                logo.Locale = sessionStore.getLocaleFromBackendCode(logo.Locale);
                return logo;
            });
            return item;
        });

        const sites = allSites.filter(p => !IsNullOrWhiteSpace(p.ParentSiteId));
        const parentSites = allSites.filter(p => IsNullOrWhiteSpace(p.ParentSiteId));

        if (sites && sites.length > 0) {
            const sitesModel: SitesModel[] = [];
            const parentSitesModel: SitesModel[] = [];

            parentSites.sortByProperty(c => c.ClinicalName.toLowerCase()).forEach(p => {
                parentSitesModel.push(new SitesModel(p));
                sitesModel.push(new SitesModel(p));

                sites.sortByProperty(c => c.ClinicalName.toLowerCase()).forEach(s => {
                    if (s.ParentSiteId === p.Id) {
                        sitesModel.push(new SitesModel(s));
                    }
                });
            });

            store.sitesTabModel = store.sitesTabModel.set("Sites", Immutable.List(sitesModel));
            store.sitesTabModel = store.sitesTabModel.set("ParentSites", Immutable.List(parentSitesModel));

            cacheStore.refreshSites(response.Data);
        }
    }

    private upsertSite(response: ServerEvent, store: AdministrationStore): void {
        const isDuplicate = response.Data["IsDuplicate"];

        if (isDuplicate) {
            store.sitesTabModel = store.sitesTabModel.set("IsDuplicateWarningOpen", isDuplicate);
            store.sitesTabModel = store.sitesTabModel.set("DuplicateDto", response.Data["SiteDto"]);

            sessionStore.showGlobalOverlay(true);
        }
        else {
            const responseData = response.Data["Site"];

            responseData.LogoAttachments = responseData?.LogoAttachments?.map(logo => {
                logo.Locale = sessionStore.getLocaleFromBackendCode(logo.Locale);
                return logo;
            });

            const site: Site = responseData;

            const sites = store.sitesTabModel.get("Sites").toArray();
            if (!sites.some(c => c.Model.Id === site.Id)) {
                sites.push(new SitesModel(site));
            }

            store.sitesTabModel = store.sitesTabModel.set("UIStateChange", SitesUIState.View);
            store.siteViewModel = store.siteViewModel.set("Model", site);

            store.sitesTabAction.loadSites();
        }
    }

    private getCostCenters(response: ServerEvent, store: AdministrationStore): void {
        const costCenters: PrivateBillCostCenter[] = response.Data["CostCenters"];
        store.siteViewModel = store.siteViewModel.set("CostCenters", Immutable.List(costCenters));
    }

    private saveLogo(response: ServerEvent, store: AdministrationStore): void {
        const responseData = response.Data;
        const success = responseData["IsSuccess"];

        if (success) {
            const sites = store.sitesTabModel.get("Sites").toArray();
            const logoAttachments = responseData["LogoAttachments"] ?? [];

            const defaultSiteId = responseData["DefaultSiteId"];

            const allSiteIds = [
                ...logoAttachments
                    .map(item => item.SiteId),
                defaultSiteId
            ];

            const uniqueSiteIds = Array.from(new Set(allSiteIds));

            uniqueSiteIds.forEach(siteId => {
                const site = sites.firstOrDefault(c => c.Model.Id === siteId);
                const responseDataLogoAttachments = logoAttachments.map(item => ({
                    Locale: sessionStore.getLocaleFromBackendCode(item.Locale),
                    AttachmentId: item.AttachmentId,
                    SiteId: item.SiteId
                }));

                if (site) {
                    site.Model.LogoAttachments = responseDataLogoAttachments;
                    store.sitesTabModel = store.sitesTabModel.set("UIStateChange", SitesUIState.View);

                    if (siteId === logoAttachments[0]?.SiteId || siteId === defaultSiteId) {
                        store.siteViewModel = store.siteViewModel.set("Model", site.Model);
                    }
                }
            });

            store.sitesTabAction.loadSites();
        }
    }
}

export { SitesTabResponseHandler };
